import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Gift } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { objectPosition } from "@/lib/focal";
import ProductCard from "@/components/store/ProductCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function GiftBanner() {
  return (
    <div className="border border-brand/30 bg-brand-light/60 p-5 mt-8 flex flex-wrap items-center gap-4" data-testid="gift-services-banner">
      <Gift size={22} className="text-brand shrink-0" />
      <p className="text-sm text-stone-700 flex-1 min-w-56">
        Every order can be <strong>gift-wrapped</strong> with a <strong>personal message</strong>, and you can
        <strong> hide prices on the invoice</strong> — select these at checkout. Add an engraving to make it one-of-one.
      </p>
    </div>
  );
}

export default function GiftsHub() {
  const { config } = useStore();
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState({ recipient: "", occasion: "", budget: "" });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Jewellery Gifts — by Recipient, Occasion & Budget | Siyaara";
    api.get("/gifts/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const find = async () => {
    setBusy(true);
    try {
      const r = await api.get("/gifts/find", { params: sel });
      setResults(r.data);
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="container-x py-20 text-center text-stone-400 font-mono text-sm">LOADING…</div>;

  return (
    <div className="container-x py-10" data-testid="gifts-hub">
      <p className="eyebrow !text-brand">THE GIFT SHOP</p>
      <h1 className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight mt-2 max-w-2xl">
        A gift that says their <em className="font-script not-italic text-brand">name</em>
      </h1>
      <p className="text-stone-600 mt-3 max-w-2xl">Engravable, birthstoned, gift-wrapped and price-hidden — find the piece in three taps.</p>

      {config?.brand_imagery?.gifting_hero && (
        <div className="mt-8 rounded-2xl overflow-hidden h-64 sm:h-80 relative" data-testid="gifts-hero">
          <img src={config.brand_imagery.gifting_hero} alt="Siyaara signature gifting"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: objectPosition(config.brand_imagery.gifting_hero, config) }} />
        </div>
      )}

      {/* Gift finder */}
      <div className="border border-stone-200 bg-white p-6 mt-8" data-testid="gift-finder">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <p className="eyebrow mb-2">WHO'S IT FOR</p>
            <Select value={sel.recipient} onValueChange={(v) => setSel({ ...sel, recipient: v })}>
              <SelectTrigger data-testid="finder-recipient"><SelectValue placeholder="Recipient" /></SelectTrigger>
              <SelectContent>{cfg.recipients.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`finder-recipient-${r.key}`}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="eyebrow mb-2">OCCASION</p>
            <Select value={sel.occasion} onValueChange={(v) => setSel({ ...sel, occasion: v })}>
              <SelectTrigger data-testid="finder-occasion"><SelectValue placeholder="Occasion" /></SelectTrigger>
              <SelectContent>{cfg.occasions.map((o) => <SelectItem key={o.key} value={o.key} data-testid={`finder-occasion-${o.key}`}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="eyebrow mb-2">BUDGET</p>
            <Select value={sel.budget} onValueChange={(v) => setSel({ ...sel, budget: v })}>
              <SelectTrigger data-testid="finder-budget"><SelectValue placeholder="Budget" /></SelectTrigger>
              <SelectContent>{cfg.price_bands.map((b) => <SelectItem key={b.key} value={b.key} data-testid={`finder-budget-${b.key}`}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="bg-stone-900 hover:bg-brand text-white btn-press h-10 rounded-none" onClick={find} disabled={busy} data-testid="finder-search-btn">
            {busy ? "Finding…" : "Find Gifts"}
          </Button>
        </div>
        {results && (
          <div className="mt-6">
            <p className="text-sm font-mono text-stone-500" data-testid="finder-count">{results.total} MATCHES</p>
            {results.total === 0 ? (
              <p className="text-stone-500 text-sm mt-3">No exact matches — try widening the budget.</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {results.items.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        <section>
          <h2 className="font-heading font-semibold text-lg mb-3">By recipient</h2>
          <div className="flex flex-wrap gap-2">
            {cfg.recipients.map((r) => (
              <Link key={r.key} to={`/gifts/${r.key}`} className="border border-stone-200 bg-white px-4 py-2 text-sm hover:border-brand hover:text-brand transition-colors" data-testid={`gift-recipient-${r.key}`}>{r.label}</Link>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-heading font-semibold text-lg mb-3">By occasion</h2>
          <div className="flex flex-wrap gap-2">
            {cfg.occasions.map((o) => (
              <Link key={o.key} to={`/gifts/${o.key}`} className="border border-stone-200 bg-white px-4 py-2 text-sm hover:border-brand hover:text-brand transition-colors" data-testid={`gift-occasion-${o.key}`}>{o.label}</Link>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-heading font-semibold text-lg mb-3">By budget</h2>
          <div className="flex flex-wrap gap-2">
            {cfg.price_bands.map((b) => (
              <Link key={b.key} to={`/gifts/${b.key}`} className="border border-stone-200 bg-white px-4 py-2 text-sm hover:border-brand hover:text-brand transition-colors" data-testid={`gift-budget-${b.key}`}>{b.label}</Link>
            ))}
          </div>
        </section>
      </div>

      <GiftBanner />
    </div>
  );
}

export function GiftPage() {
  const { key } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null); setNotFound(false);
    api.get(`/gifts/${key}`)
      .then((r) => { setPage(r.data); document.title = `${r.data.title} | Siyaara`; })
      .catch(() => setNotFound(true));
  }, [key]);

  if (notFound) return <div className="container-x py-20 text-center" data-testid="gift-not-found">Gift page not found.</div>;
  if (!page) return <div className="container-x py-20 text-center text-stone-400 font-mono text-sm">LOADING…</div>;

  return (
    <div className="container-x py-8" data-testid="gift-page">
      <p className="eyebrow !text-brand">GIFTS · {page.kind?.toUpperCase()}</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="gift-h1">{page.title}</h1>
      <p className="text-stone-600 mt-3 max-w-3xl leading-relaxed">{page.intro}</p>
      <p className="text-xs font-mono text-stone-500 mt-2" data-testid="gift-count">{page.count} PIECES · LIVE PRICES</p>
      {page.products.length === 0 ? (
        <p className="text-stone-500 mt-8">No pieces in this range right now — rates move daily, check back soon.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {page.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
      <GiftBanner />
    </div>
  );
}
